import { type ReactNode, useEffect, useMemo, useState } from 'react';

import { Text } from '@revelio/ui';

import { AlertDialog } from '../../../../shared/components/alert-dialog';
import { DEFAULT_ERROR_MSG } from '../../../../shared/ui/urql-and-tanstack-error-message/urql-and-tanstack-error-message';
import { useGetTabs } from '../TabManager/useTabs';
import styles from './FullPageError.module.css';
import { FullPageErrorContext } from './full-page-error.context';

export const DEFAULT_FULL_PAGE_ERROR_THRESHOLD = 0.66; // error when 2 out of 3 charts errored

export const FullPageError = ({
  showErrorRatio,
  children,
}: {
  showErrorRatio: number;
  children: ReactNode;
}) => {
  const tabs = useGetTabs();
  const currentTab = tabs.find((tab) => tab.isActive);
  const plotsOnTab = currentTab?.tabContent?.items;
  const [itemErrors, setItemErrors] = useState<string[]>([]);
  const [showFullPageError, setShowFullPageError] = useState(false);
  const [hasShownErrorForTab, setHasShownErrorForTab] = useState(false);

  const chartErrorValue = useMemo(() => {
    return {
      chartErrored: (chartId: string) => {
        setItemErrors((prev) => {
          const uniqueErrors = new Set(prev);
          uniqueErrors.add(chartId);
          return Array.from(uniqueErrors);
        });
      },
      resetChartError: (chartId: string) => {
        setItemErrors((prev) => {
          return prev.filter((id) => id !== chartId);
        });
      },
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    setHasShownErrorForTab(false);
    setShowFullPageError(false);
    setItemErrors([]);
  }, [plotsOnTab]);

  useEffect(() => {
    if (hasShownErrorForTab) {
      return;
    }

    const totalItems = plotsOnTab?.length ?? 0;
    if (totalItems > 0 && itemErrors.length / totalItems >= showErrorRatio) {
      setShowFullPageError(true);
    }
  }, [itemErrors, plotsOnTab, showErrorRatio, hasShownErrorForTab]);

  return (
    <FullPageErrorContext.Provider value={chartErrorValue}>
      {children}
      <AlertDialog
        isOpen={showFullPageError}
        onClose={() => {
          setShowFullPageError(false);
          setHasShownErrorForTab(true);
        }}
        alertType="actionless"
        headerText="Sorry!"
        bodyContent={<Text className={styles.body}>{DEFAULT_ERROR_MSG}</Text>}
        skipDefaultDisclosure={true}
      />
    </FullPageErrorContext.Provider>
  );
};
